import { useCallback, useRef } from "react";
import type {
  NeutralActivityDto,
  OperationDto,
  ProductDto,
  UnitDto,
  ValidCombinationDto,
} from "@normax-performance/shared-types";
import type { PerformanceZoneThresholds } from "@normax-performance/shared-format";
import {
  emptyNeutralRow,
  emptyNormedRow,
  emptyUnnormedRow,
  isNeutralRow,
  isNormedRow,
  isUnnormedRow,
  type RowState,
} from "../daily-entry.types";
import { Plus } from "lucide-react";
import { Button } from "../../../shared-ui/primitives";
import { NormedWorkRow } from "./NormedWorkRow";
import { NeutralActivityRow } from "./NeutralActivityRow";
import { UnnormedWorkRow } from "./UnnormedWorkRow";
import "./EntryGrid.css";

/** Used only while the real company-performance-settings fetch (in DailyEntryPage) hasn't resolved yet. */
const FALLBACK_THRESHOLDS: PerformanceZoneThresholds = { lowerNeutralThreshold: 90, aboveNormThreshold: 100 };

/**
 * Per-field ref + Enter-to-advance handler for one logical field slot within
 * a row, resolved by `EntryGrid` (the sole owner of the cross-row field
 * registry, extended from its pre-existing single-ref-per-row `fieldRefs`
 * below) and handed to row components — never a second, parallel navigation
 * system (task: docs/NORMAX_PERFORMANCE_DAILY_ENTRY_ENTER_KEYBOARD_FLOW_FORENSIC_REPORT.md
 * §13). `index` is the field's position in that row TYPE's real, fixed
 * logical order (§2/§9 of the same report) — row components own knowing
 * which index is which field; `EntryGrid` only ever sees "index N of row X."
 */
export interface RowFocusNav {
  field(index: number): {
    ref: (el: HTMLInputElement | null) => void;
    onKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  };
}

export interface EntryGridProps {
  rows: RowState[];
  onRowsChange: (rows: RowState[]) => void;
  /**
   * Deleting a row that has a `serverId` (already persisted) is never
   * silent (2026-09-09 brainstorm) — routed to the parent instead of
   * removed here directly, since only the parent (`DailyEntryPage`) can
   * prompt for the mandatory reason and call the real DELETE endpoint. A
   * row with no `serverId` (never saved — nothing to lose) is still
   * removed immediately, locally, exactly as before, with no callback.
   */
  onDeleteRow?: (row: RowState) => void;
  products: ProductDto[];
  operations: OperationDto[];
  neutralActivities: NeutralActivityDto[];
  units: UnitDto[];
  validCombinations: ValidCombinationDto[];
  readOnly?: boolean;
  /** From GET /company-performance-settings/current for the entry's work_date. */
  thresholds?: PerformanceZoneThresholds;
}

export function EntryGrid({
  rows,
  onRowsChange,
  onDeleteRow,
  products,
  operations,
  neutralActivities,
  units,
  validCombinations,
  readOnly,
  thresholds = FALLBACK_THRESHOLDS,
}: EntryGridProps) {
  // One ordered array of field refs per row (localId -> slot 0..N, index
  // meaning owned by each row TYPE's component — see RowFocusNav above).
  // Extends the pre-existing single-ref "focus the primary field after
  // add/duplicate" mechanism into the same registry the new Enter-to-next
  // flow uses, rather than maintaining two.
  const fieldRefs = useRef(new Map<string, (HTMLInputElement | null)[]>());

  const setFieldRef = useCallback((localId: string, index: number, el: HTMLInputElement | null) => {
    const arr = fieldRefs.current.get(localId) ?? [];
    arr[index] = el;
    fieldRefs.current.set(localId, arr);
  }, []);

  // Unchanged behavior for existing callers (add/duplicate) — the primary
  // field is always slot 0 for every row type (Proizvod / Vrsta / Operacija).
  const focusRow = useCallback((localId: string) => {
    requestAnimationFrame(() => fieldRefs.current.get(localId)?.[0]?.focus());
  }, []);

  const updateRow = useCallback(
    (localId: string, patch: Partial<RowState>) => {
      onRowsChange(
        rows.map((r) => (r.localId === localId ? ({ ...r, ...patch, dirty: true } as RowState) : r)),
      );
    },
    [rows, onRowsChange],
  );

  const deleteRow = useCallback(
    (localId: string) => {
      const row = rows.find((r) => r.localId === localId);
      if (row?.serverId && onDeleteRow) {
        onDeleteRow(row);
        return;
      }
      onRowsChange(rows.filter((r) => r.localId !== localId));
    },
    [rows, onRowsChange, onDeleteRow],
  );

  const duplicateRow = useCallback(
    (localId: string) => {
      const source = rows.find((r) => r.localId === localId);
      if (!source) return;
      const copy: RowState = {
        ...source,
        localId: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        serverId: null,
        durationMinutes: "",
        ...(isNormedRow(source) || isUnnormedRow(source) ? { reportedQuantity: "" } : {}),
      } as RowState;
      const index = rows.findIndex((r) => r.localId === localId);
      const next = [...rows];
      next.splice(index + 1, 0, copy);
      onRowsChange(next);
      focusRow(copy.localId);
    },
    [rows, onRowsChange, focusRow],
  );

  const addRow = useCallback(
    (type: "NORMED_WORK" | "NEUTRAL_ACTIVITY" | "UNNORMED_WORK", afterLocalId?: string) => {
      const row = type === "NORMED_WORK" ? emptyNormedRow() : type === "NEUTRAL_ACTIVITY" ? emptyNeutralRow() : emptyUnnormedRow();
      if (afterLocalId) {
        const index = rows.findIndex((r) => r.localId === afterLocalId);
        const next = [...rows];
        next.splice(index + 1, 0, row);
        onRowsChange(next);
      } else {
        onRowsChange([...rows, row]);
      }
      focusRow(row.localId);
      return row.localId;
    },
    [rows, onRowsChange, focusRow],
  );

  // Plain-Enter-to-next-field (task:
  // docs/NORMAX_PERFORMANCE_DAILY_ENTRY_ENTER_KEYBOARD_FLOW_FORENSIC_REPORT.md
  // §8/§10/§13). Advances to the next REGISTERED field after `index` in this
  // row — skipping any slot that's empty (a conditionally-unrendered field,
  // e.g. Normed's collapsed-away Broj radnog naloga/Napomena, or a field that
  // simply hasn't mounted yet) by construction, since only mounted fields
  // ever call `setFieldRef`. If there is no further field in this row, this
  // mirrors the existing, already-shipped `Ctrl+Enter` gesture exactly (same
  // `addRow` call, same target) rather than inventing a new destination —
  // never Sačuvaj/Završi dan, which this never touches or focuses.
  const advanceFocus = useCallback(
    (localId: string, index: number, entryType: RowState["entryType"]) => {
      const arr = fieldRefs.current.get(localId);
      const next = arr?.slice(index + 1).find((el): el is HTMLInputElement => !!el);
      if (next) {
        next.focus();
        return;
      }
      if (readOnly) return;
      addRow(entryType, localId);
    },
    [addRow, readOnly],
  );

  // `advanceFocus`'s own identity changes on every keystroke (it depends on
  // `addRow`, which depends on `rows`) — routing through a ref keeps the
  // cached per-field callbacks below permanently stable regardless, so
  // React never sees a field's `ref`/`onKeyDown` prop "change" on an
  // ordinary keystroke and therefore never unmounts/remounts that field's
  // ref registration for it. Always reads the CURRENT implementation.
  const latestHandlers = useRef({ setFieldRef, advanceFocus });
  latestHandlers.current = { setFieldRef, advanceFocus };

  // One cached `{ ref, onKeyDown }` per (row, field index) — created once,
  // reused for the row's whole lifetime, so a row component reading the
  // same field twice in one render (once for `ref`, once for `onKeyDown`)
  // gets the SAME object, and every later render does too. Never cleaned up
  // on row deletion — a handful of closures per deleted row's fields is
  // negligible at Daily Entry's real scale (well under a hundred rows/day)
  // and not worth the added bookkeeping.
  const focusNavFieldCache = useRef(new Map<string, ReturnType<RowFocusNav["field"]>>());

  // Row components resolve this into per-field `{ ref, onKeyDown }` props —
  // see RowFocusNav above. `undefined` in readOnly mode: every row already
  // swaps its interactive fields for plain read-only `<span>`s then, so
  // there is nothing to register or advance between.
  const makeFocusNav = useCallback((localId: string, entryType: RowState["entryType"]): RowFocusNav => {
    return {
      field: (index: number) => {
        const cacheKey = `${localId}:${index}`;
        const cached = focusNavFieldCache.current.get(cacheKey);
        if (cached) return cached;
        const entry = {
          ref: (el: HTMLInputElement | null) => latestHandlers.current.setFieldRef(localId, index, el),
          onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
            // Guard modifier keys explicitly — Ctrl+Enter (add row), Ctrl+D
            // (duplicate), Ctrl+Shift+Backspace (delete) are handled entirely
            // by `handleRowKeyDown` below, via normal DOM bubbling from this
            // same field up to the row wrapper `<div>`'s `onKeyDown`. This
            // handler must never `preventDefault`/`stopPropagate` any of
            // them — only a bare Enter is ours to act on.
            if (event.key !== "Enter" || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
            event.preventDefault();
            event.stopPropagation();
            latestHandlers.current.advanceFocus(localId, index, entryType);
          },
        };
        focusNavFieldCache.current.set(cacheKey, entry);
        return entry;
      },
    };
  }, []);

  const handleRowKeyDown = useCallback(
    (localId: string, entryType: RowState["entryType"]) => (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (readOnly) return;
      if (event.ctrlKey && event.key === "Enter") {
        event.preventDefault();
        addRow(entryType === "NORMED_WORK" ? "NORMED_WORK" : entryType, localId);
      } else if (event.ctrlKey && (event.key === "d" || event.key === "D")) {
        event.preventDefault();
        duplicateRow(localId);
      } else if (event.ctrlKey && event.shiftKey && event.key === "Backspace") {
        event.preventDefault();
        deleteRow(localId);
      }
    },
    [readOnly, addRow, duplicateRow, deleteRow],
  );

  const normedRows = rows.filter(isNormedRow);
  const neutralRows = rows.filter(isNeutralRow);
  const unnormedRows = rows.filter(isUnnormedRow);

  if (rows.length === 0) {
    return (
      <div className="nx-entry-grid__empty-day">
        <p>Nema unetih redova za ovaj dan.</p>
        {!readOnly ? (
          <div className="nx-entry-grid__empty-actions">
            <Button variant="primary" onClick={() => addRow("NORMED_WORK")}>
              <Plus size={16} />
              Dodaj prvi red
            </Button>
            <Button variant="secondary" onClick={() => addRow("NEUTRAL_ACTIVITY")}>
              <Plus size={16} />
              Neutralna aktivnost
            </Button>
            <Button variant="secondary" onClick={() => addRow("UNNORMED_WORK")}>
              <Plus size={16} />
              Rad bez norme
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="nx-entry-grid">
      <section className="nx-entry-grid__section">
        <div className="nx-entry-grid__table-header">
          <span />
          <span>Proizvod</span>
          <span>Operacija</span>
          <span>Vreme</span>
          <span>Ostvareno</span>
          <span>Planirano</span>
          <span>%</span>
        </div>
        {normedRows.length === 0 ? (
          <p className="nx-entry-grid__section-empty">(nema normiranih redova)</p>
        ) : (
          normedRows.map((row, i) => (
            <NormedWorkRow
              key={row.localId}
              index={i + 1}
              row={row}
              validCombinations={validCombinations}
              onChange={(patch) => updateRow(row.localId, patch)}
              onDelete={() => deleteRow(row.localId)}
              readOnly={readOnly}
              thresholds={thresholds}
              focusNav={readOnly ? undefined : makeFocusNav(row.localId, "NORMED_WORK")}
              onKeyDown={handleRowKeyDown(row.localId, "NORMED_WORK")}
            />
          ))
        )}
        {!readOnly ? (
          <div className="nx-entry-grid__section-actions">
            <Button size="sm" variant="secondary" onClick={() => addRow("NORMED_WORK")}>
              <Plus size={16} />
              Novi normirani red
            </Button>
            {normedRows.length > 0 ? (
              <Button size="sm" variant="ghost" onClick={() => duplicateRow(normedRows[normedRows.length - 1]!.localId)}>
                + Dupliraj poslednji red
              </Button>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="nx-entry-grid__section nx-entry-grid__section--neutral">
        <h3 className="nx-entry-grid__section-title">Neutralne aktivnosti</h3>
        {neutralRows.length === 0 ? (
          <p className="nx-entry-grid__section-empty">(nema unosa za ovaj dan)</p>
        ) : (
          neutralRows.map((row, i) => (
            <NeutralActivityRow
              key={row.localId}
              index={i + 1}
              row={row}
              neutralActivities={neutralActivities}
              onChange={(patch) => updateRow(row.localId, patch)}
              onDelete={() => deleteRow(row.localId)}
              readOnly={readOnly}
              focusNav={readOnly ? undefined : makeFocusNav(row.localId, "NEUTRAL_ACTIVITY")}
              onKeyDown={handleRowKeyDown(row.localId, "NEUTRAL_ACTIVITY")}
            />
          ))
        )}
        {!readOnly ? (
          <Button size="sm" variant="secondary" onClick={() => addRow("NEUTRAL_ACTIVITY")}>
            <Plus size={16} />
            Dodaj neutralnu aktivnost
          </Button>
        ) : null}
      </section>

      <section className="nx-entry-grid__section nx-entry-grid__section--unnormed">
        <h3 className="nx-entry-grid__section-title">Rad bez norme</h3>
        {unnormedRows.length === 0 ? (
          <p className="nx-entry-grid__section-empty">(nema unosa za ovaj dan)</p>
        ) : (
          unnormedRows.map((row, i) => (
            <UnnormedWorkRow
              key={row.localId}
              index={i + 1}
              row={row}
              operations={operations}
              products={products}
              units={units}
              onChange={(patch) => updateRow(row.localId, patch)}
              onDelete={() => deleteRow(row.localId)}
              readOnly={readOnly}
              focusNav={readOnly ? undefined : makeFocusNav(row.localId, "UNNORMED_WORK")}
              onKeyDown={handleRowKeyDown(row.localId, "UNNORMED_WORK")}
            />
          ))
        )}
        {!readOnly ? (
          <Button size="sm" variant="secondary" onClick={() => addRow("UNNORMED_WORK")}>
            <Plus size={16} />
            Dodaj rad bez norme
          </Button>
        ) : null}
      </section>
    </div>
  );
}
